import { z } from 'zod';

// Publishing configuration options
export const layoutOptionsDTOSchema = z.object({
  title: z.boolean(),
  logo: z.boolean(),
  publishDate: z.boolean(),
  legend: z.boolean(),
});

export type LayoutOptionsDTO = z.infer<typeof layoutOptionsDTOSchema>;

export const autoPublishOptions = [
  'never',
  'hourly',
  'daily',
  'weekly',
  'monthly',
  'realtime',
] as const;
export const autoPublishSchema = z.enum(autoPublishOptions);

export type AutoPublish = z.infer<typeof autoPublishSchema>;

export const publishTypeOptions = ['live', 'locked', 'image'] as const;
export const publishTypeSchema = z.enum(publishTypeOptions);

export type PublishType = z.infer<typeof publishTypeSchema>;

export const weekDayOptions = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export const weekDaySchema = z.enum(weekDayOptions);

export type WeekDay = z.infer<typeof weekDaySchema>;

export const monthWeekOptions = ['1st', '2nd', '3rd', '4th'] as const;
export const monthWeekSchema = z.enum(monthWeekOptions);

export type MonthWeek = z.infer<typeof monthWeekSchema>;

// Authentication type discriminator
export const authTypeOptions = ['none', 'password'] as const;
export const authTypeSchema = z.enum(authTypeOptions);

export type AuthType = z.infer<typeof authTypeSchema>;

// Config as stored in database (internal use only - includes sensitive fields)
export const publishConfigDTOSchema = z.object({
  layoutOptions: layoutOptionsDTOSchema,
  visionStatement: z.string().max(1000).optional(),
  tempoBranding: z.boolean(),
  autoPublish: autoPublishSchema,
  weeklyPublishDay: weekDaySchema.optional(),
  monthlyPublishWeek: monthWeekSchema.optional(),
  monthlyPublishDay: weekDaySchema.optional(),
  // Auth fields (stored)
  authType: authTypeSchema.default('none'),
  passwordHash: z.string().optional(), // Stored hash, NEVER returned to client
});

export type PublishConfigDTO = z.infer<typeof publishConfigDTOSchema>;

// Config for API responses (excludes sensitive fields)
export const publishConfigResponseDTOSchema = publishConfigDTOSchema.omit({
  passwordHash: true, // Security: passwordHash never leaves the server
});

export type PublishConfigResponseDTO = z.infer<typeof publishConfigResponseDTOSchema>;

// Password operation discriminated union - makes illegal states unrepresentable
export const passwordOperationSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('keep') }), // Preserve existing (default if omitted)
  z.object({ operation: z.literal('set'), password: z.string().min(1) }), // Set new password
  z.object({ operation: z.literal('remove') }), // Clear password protection
]);

export type PasswordOperation = z.infer<typeof passwordOperationSchema>;

// Config for upsert requests (input from client)
export const publishConfigUpsertDTOSchema = publishConfigDTOSchema
  .omit({ authType: true, passwordHash: true }) // Client doesn't set these directly
  .extend({
    // Password management using discriminated union
    passwordOperation: passwordOperationSchema.optional(),
  });

export type PublishConfigUpsertDTO = z.infer<typeof publishConfigUpsertDTOSchema>;

// Published view DTO (internal use - includes sensitive config fields)
export const publishedViewDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  urlToken: z.string().length(12), // nanoid(12)
  userId: z.string().min(1),
  type: publishTypeSchema,
  config: publishConfigDTOSchema, // Internal: includes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PublishedViewDTO = z.infer<typeof publishedViewDTOSchema>;

// Published view response DTO (for API responses - excludes sensitive fields)
export const publishedViewResponseDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  urlToken: z.string().length(12),
  userId: z.string().min(1),
  type: publishTypeSchema,
  config: publishConfigResponseDTOSchema, // Security: excludes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PublishedViewResponseDTO = z.infer<typeof publishedViewResponseDTOSchema>;

// Upsert DTO (for creating or updating a published view)
export const publishedViewUpsertDTOSchema = z
  .object({
    type: publishTypeSchema,
    config: publishConfigUpsertDTOSchema,
  })
  .strict();

export type PublishedViewUpsertDTO = z.infer<typeof publishedViewUpsertDTOSchema>;
